/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 03/09/2025 - 22:16:59
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 03/09/2025
    * - Author          : 
    * - Modification    : 
**/


import React, { useState, useEffect } from "react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { StripeError, PaymentIntent } from "@stripe/stripe-js";
import Navbar from "../shared/navbar";
import Footer from "../shared/footer";
import { db, auth } from "../../firebase";
import { doc, setDoc } from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../shared/loader";
import { api } from "../../api/transaction";
import { toast } from "react-toastify";
import axios, { AxiosError } from "axios";

interface VoucherDetails {
  code: string;
  type: 'percentage' | 'fixed';
  value: string;
}

interface VoucherResponse {
  finalAmount: number;
  originalAmount: number;
  discountAmount: number;
  currency: string;
  paymentIntentId: string;
  clientSecret: string;
  voucher: VoucherDetails;
}

interface LocationState {
  paymentIntents_ID: string;
  SimulationId: string;
}

const CheckoutForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [couponID, setCouponId] = useState("");
  const [error, setError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [apply, setApply] = useState("Apply");
  const [originalAmount, setOriginalAmount] = useState(19);
  const [finalAmount, setFinalAmount] = useState(19);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [appliedVoucher, setAppliedVoucher] = useState<VoucherDetails | null>(null);
  const [isFreeOrder, setIsFreeOrder] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const [paymentElementOptions, setPaymentElementOptions] = useState({
    paymentMethodOrder: ['card', 'paypal'] as string[]
  });


  // Validate payment intent on load
  useEffect(() => {
    if (!locationState?.paymentIntents_ID) {
      console.error("No payment intent ID found in location state");
      setError("Payment setup is incomplete. Please try again.");
    }
  }, [locationState]);

  // Handle transaction and store data into database
// Handle transaction and store data into database
const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
  e.preventDefault();
  setIsLoading(true);
  setError("");

  if (!stripe || !elements) {
    console.error("Stripe or elements not available");
    setError("Payment cannot be processed. Please refresh the page and try again.");
    setIsLoading(false);
    return;
  }

  try {
    // Confirm the payment
    const { paymentIntent, error }: { paymentIntent?: PaymentIntent; error?: StripeError } =
      await stripe.confirmPayment({
        elements,
        // confirmParams: {
        //   return_url: "https://pv-glarecheck.com/thank-you", 
        // },
        redirect: "if_required", 
      });

    if (error) {
      console.error("Error confirming payment:", error);
      setError(`Payment error: ${error.message}`);
      setIsLoading(false);
      return;
    }
    
    if (paymentIntent && paymentIntent.status === "succeeded") {
      
      // Store transaction details in Firestore
      await setDoc(
        doc(db, "paidReport", locationState.SimulationId),
        {
          transactionID: paymentIntent.id,
          owner: auth.currentUser?.uid,
          amount: paymentIntent.amount / 100, 
          originalAmount: originalAmount,
          discountAmount: discountAmount,
          voucherApplied: appliedVoucher ? appliedVoucher.code : null,
          paymentStatus: true,
          createdAt: new Date(paymentIntent.created * 1000), 
        },
        { merge: true }
      );

      // Create invoice for the payment
      try {
        console.log("Creating invoice for payment:", paymentIntent.id);
        const invoiceResponse = await api.createInvoiceForPayment(paymentIntent.id);
        console.log("Invoice created successfully:", invoiceResponse.data);
        
        // You could store the invoice ID in Firestore if needed
        if (invoiceResponse.data?.data?.invoiceId) {
          await setDoc(
            doc(db, "paidReport", locationState.SimulationId),
            {
              invoiceId: invoiceResponse.data.data.invoiceId,
              invoiceUrl: invoiceResponse.data.data.invoiceUrl,
              invoicePdfUrl: invoiceResponse.data.data.invoicePdfUrl
            },
            { merge: true }
          );
        }
      } catch (invoiceError) {
        // Log the error but don't block the success flow
        console.error("Error creating invoice:", invoiceError);
        // We could store this error for later retry if needed
      }

      // Clean up and redirect
      localStorage.removeItem("stripeClientSecret");
      toast.success("Payment successful!");
      // window.location.href = "https://pv-glarecheck.com/thank-you";
    } else {
      setError("Payment not completed. Please try again.");
    }    
  } catch (err) {
    console.error("Unexpected payment error:", err);
    setError("An unexpected error occurred during payment. Please try again later.");
  } finally {
    setIsLoading(false);
  }
};
  
  // Apply voucher code
  const ApplyCoupon = async () => {
    if (!couponID) {
      toast.warning("Please enter the voucher code");
      return;
    }
    
    if (!locationState?.paymentIntents_ID) {
      toast.error("Payment setup is incomplete. Please refresh the page.");
      return;
    }
    
    setCouponLoading(true);
    
    try {
      
      const response = await api.applyVoucher({
        voucherCode: couponID,
        paymentIntentId: locationState.paymentIntents_ID,
      });
      
      const data = response.data;
      
      // Update the state with all the details
      setFinalAmount(data.finalAmount / 100);
      setOriginalAmount(data.originalAmount / 100);
      setDiscountAmount(data.discountAmount / 100);
      setAppliedVoucher(data.voucher);
      
      // Check if order is completely free
      if (data.finalAmount === 0) {
        setIsFreeOrder(true);
        toast.success(`Voucher ${data.voucher.code} applied - Your order is FREE!`);
      } else {
        // Regular discount
        setApply(`Applied: ${data.voucher.value} off`);
        toast.success(`Voucher ${data.voucher.code} applied successfully!`);
      }
    } catch (error) {
      console.error("Voucher application error:", error);
      setApply("Apply");
      
      // Enhanced error handling
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        
        if (axiosError.response?.data) {
          const data = axiosError.response.data as any;
          
          if (data.message) {
            toast.error(data.message);
          } else if (data.error?.message) {
            toast.error(data.error.message);
          } else if (axiosError.response?.status === 404) {
            toast.error("Invalid voucher code");
          } else {
            toast.error("Failed to apply voucher. Please try again.");
          }
        } else {
          toast.error("Connection error. Please try again.");
        }
      } else {
        toast.error("An unexpected error occurred");
      }
    } finally {
      setCouponLoading(false);
    }
  };



  const completeOrderAsFree = async () => {
    if (!appliedVoucher) {
      toast.error("No voucher applied");
      return;
    }
    
    if (!locationState?.SimulationId) {
      toast.error("Project ID is missing. Please try again.");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await api.completeFreeOrder({
        voucherCode: appliedVoucher.code,
        originalAmount: originalAmount * 100,
        projectId: locationState.SimulationId,
        userId: auth.currentUser?.uid,
      });
    
      
      const documentData: {
        transactionID: string;
        owner: string | undefined;
        amount: number;
        originalAmount: number;
        discountAmount: number;
        voucherApplied: string;
        paymentStatus: boolean;
        createdAt: Date;
        invoiceId?: string;
        invoiceUrl?: string;
        invoicePdfUrl?: string;
      } = {
        transactionID: response.data.data.transactionId || `free_${Date.now()}`, 
        owner: auth.currentUser?.uid,
        amount: 0,
        originalAmount: originalAmount,
        discountAmount: discountAmount,
        voucherApplied: appliedVoucher.code,
        paymentStatus: true,
        createdAt: new Date(),
      };
      
      if (response.data.data && response.data.data.invoice) { 
        documentData.invoiceId = response.data.data.invoice.invoiceId;
        documentData.invoiceUrl = response.data.data.invoice.invoiceUrl;
        documentData.invoicePdfUrl = response.data.data.invoice.invoicePdfUrl;
      }

      await setDoc(
        doc(db, "paidReport", locationState.SimulationId),
        documentData,
        { merge: true }
      );
      
      // Redirect to success page
      toast.success("Your free order has been processed!");
      window.location.href = "https://pv-glarecheck.com/thank-you";
    } catch (error: any) {
      console.error("Error processing free order:", error);

      const errorMessage = 
        error.response?.data?.message ||
        error.message ||
        "Failed to process your free order. Please try again.";

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Navbar />
      <div className="container mt-4 pt-4">
        <div className="text-center">
          <h4 style={{ color: "#fffff", fontWeight: "500" }}>
            The Total Cost for Your Purchase Is {finalAmount}€
          </h4>
          
          {/* Show discount info if a voucher is applied */}
          {discountAmount > 0 && (
            <div className="discount-info mt-2">
              <p className="original-price text-muted">
                <span style={{ textDecoration: "line-through" }}>{originalAmount}€</span>
                {" "}
                <span className="text-success">
                  {appliedVoucher ? appliedVoucher.value : `${discountAmount}€ off`}
                </span>
              </p>
            </div>
          )}
        </div>
        
        <form className="container checkoutForm" onSubmit={handleSubmit}>
          {/* Only show payment element if the final amount is not 0 */}
          {finalAmount > 0 && <PaymentElement options={paymentElementOptions}  />}
          
          <div className="d-flex justify-content-between mt-3">
            <input
              onChange={(e) => {
                setCouponId(e.target.value.trim());
              }}
              type="text"
              placeholder="Enter Voucher Code"
              className="couponInput"
              disabled={appliedVoucher !== null}
            />
            <button
              onClick={ApplyCoupon}
              type="button"
              className={`couponBtn text-center ${appliedVoucher ? 'btn-success' : ''}`}
              disabled={appliedVoucher !== null || couponLoading}
            >
              {couponLoading ? <Loader /> : apply}
            </button>
          </div>
          
          {appliedVoucher && (
            <button
              onClick={() => {
                setAppliedVoucher(null);
                setDiscountAmount(0);
                setFinalAmount(originalAmount);
                setApply("Apply");
                setIsFreeOrder(false);
              }}
              type="button"
              className="btn btn-sm btn-outline-danger mt-2"
            >
              Remove Voucher
            </button>
          )}
          
          {error && <small className="error">{error}</small>}
          
          {/* Conditional button based on final amount */}
          {finalAmount === 0 ? (
            <button
              onClick={completeOrderAsFree}
              type="button"
              className="d-flex justify-content-center align-items-center mb-5 payBtn btn-success"
              style={{ height: "35px" }}
              disabled={isLoading}
            >
              {isLoading ? <Loader /> : "GET FOR FREE"}
            </button>
          ) : (
            <button
              className="d-flex justify-content-center align-items-center mb-5 payBtn"
              style={{ height: "35px" }}
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? <Loader /> : `PAY ${finalAmount}€`}
            </button>
          )}
        </form>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default CheckoutForm;